import { NgFor } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import { MarkPipe, isFunction, isString, isUndefined } from '@partners/utils';
import { Observable, fromEvent, isObservable, of, pairwise, tap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, startWith, switchMap } from 'rxjs/operators';
import { SimpleFieldDirective } from '../../directives';
import { composeValidators } from '../../functions';

import { CloseScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FieldErrorComponent } from '../../components';
import { FormDefaultImportPack } from '../../import-pack';
import {
  AutoCompleteValidation,
  IFieldValidation,
  IFormConfig,
  IFormField,
  IFormFieldValueChange,
  TControlChangeTrigger,
  TFieldStateArgs,
  TIterableOption
} from '../../model';
import { ExtendedFormControl } from '../control';

function autoCompleteScrollFactory(overlay: Overlay): () => CloseScrollStrategy {
  return () => overlay.scrollStrategies.close();
}

@Component({
  selector: 'auto-complete',
  standalone: true,
  imports: [NgFor, FormDefaultImportPack, MatAutocompleteModule, MarkPipe, ScrollingModule, MatProgressBarModule],
  template: `
    <!-- ({{ fieldAccessor.control.value }}) ({{ filterControl.value }}) ({{ queryText() }}) -->
    <mat-form-field>
      <mat-label class="input-label">
        <span class="major-label">{{ fieldAccessor.label }}</span>
        <span class="minor-label">
          {{ fieldAccessor.minorLabel }}
        </span>
      </mat-label>

      <input
        matInput
        #inputElement
        simpleErrorMatcher
        (blur)="onFilterBlur($event)"
        [formControl]="filterControl"
        [type]="fieldAccessor.inputType"
        [matAutocomplete]="ngAutoComplete"
      />

      <mat-autocomplete
        [autoActiveFirstOption]="true"
        [autoSelectActiveOption]="true"
        #ngAutoComplete="matAutocomplete"
        [displayWith]="displayFn.bind(this)"
        (optionSelected)="onOptionClick($event)"
      >
        @if (isPending$$()) {
        <mat-progress-bar mode="indeterminate" class="dropdown-progress-bar"></mat-progress-bar>
        } @if (!fieldAccessor?.options$$()?.length && queryText()?.length) {
        <mat-option [value]="null">
          @if(!isPending$$() && queryText()?.length) {
          <span style="color:red">
            לא מצאנו תוצאות ל
            <span class="font--medium">"{{ queryText() }}"</span>
          </span>
          }
        </mat-option>
        }
        <mat-option *ngFor="let option of fieldAccessor.options$$(); trackBy: trackByValue" [value]="option">
          <span [innerHTML]="option[fieldAccessor.keyProp] | mark: inputElement.value"></span>

          @if(queryScope() === 'keys-values') {
          <span [innerHTML]="option[fieldAccessor.valueProp] | mark: inputElement.value"></span>
          }
        </mat-option>
      </mat-autocomplete>
      <field-reset [fieldConfig]="fieldAccessor.fieldConfig" (reset)="onManualQueryReset()"></field-reset>
      <mat-error>
        <field-error #fieldError [control]="filterControl" [fieldConfig]="fieldAccessor.fieldConfig"></field-error>
      </mat-error>
      <mat-hint>
        <field-hint [control]="filterControl" [fieldConfig]="fieldAccessor.fieldConfig"></field-hint>
      </mat-hint>
    </mat-form-field>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'autoComplete',
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: autoCompleteScrollFactory,
      deps: [Overlay]
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoCompleteComponent),
      multi: true
    }
  ],
  host: {
    class: 'auto-complete'
  }
})
export class AutoCompleteComponent<T> implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {
  @Output()
  blur = new EventEmitter();

  @Input()
  fieldAccessor: SimpleFieldDirective;

  /**
   * allow resetting this control by listening to changes to another control. we subscribe to
   * the dependency control's value changes and reset this control whenever the dependency control's
   * value is reset. example: residence & street controls where street is dependant on residence.
   * we want to reset street code & query off the auto complete when residence is reset
   */
  @Input()
  set dependantOn(dependencyCtrl: SimpleFieldDirective) {
    if (!dependencyCtrl) {
      return;
    }
    const { control } = dependencyCtrl;
    control.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.onManualQueryReset();
    });
  }

  @ViewChild('inputElement', { read: ElementRef, static: true })
  inputElement: ElementRef;

  @ViewChild(FieldErrorComponent, { read: FieldErrorComponent, static: true })
  fieldError: FieldErrorComponent<T>;

  @ViewChild('ngAutoComplete', { read: MatAutocomplete, static: true })
  ngAutoComplete: MatAutocomplete;

  error = computed<string | undefined>(() => {
    const err = this.fieldError?.error();
    return err;
  });

  #submittedRef = effect(
    () => {
      const isSubmitted = this.fieldAccessor.submitted$$();

      if (!isSubmitted) {
        return;
      }

      this.markFilterControlDirty();
    },
    { allowSignalWrites: true }
  );

  get fieldConfig(): IFormField {
    return this.fieldAccessor?.fieldConfig;
  }

  get formConfig(): IFormConfig {
    return this.fieldAccessor?.getFieldLocals().config;
  }

  get validation(): IFieldValidation {
    return this.fieldConfig.validation;
  }

  get keyProp(): string {
    return this.fieldConfig.keyProp;
  }

  get valueProp(): string {
    return this.fieldConfig.valueProp;
  }

  get debounceTime(): number {
    return this.fieldConfig.debounceInterval;
  }

  get changeTrigger(): TControlChangeTrigger {
    return this.fieldConfig.changeTrigger;
  }

  get filterElement(): HTMLInputElement {
    const { nativeElement } = this.inputElement;
    return nativeElement;
  }

  get control(): ExtendedFormControl {
    if (!this.fieldConfig) {
      return undefined;
    }
    const { control } = this.fieldConfig;
    return control;
  }

  value: T;
  disabled: boolean = false;

  /** indicates an on-going active live options filter search (example: http) */
  isPending$$ = signal<boolean>(false);

  /** the value the user types in the filter textbox */
  queryText = signal<string>('');

  /** the options search source: just keys, or keys & values */
  queryScope = input<'keys' | 'values' | 'keys-values'>('keys');

  /** a local copy of the last (current) selected option */
  #lastOption: TIterableOption;

  #onChange: (value: T | undefined) => T;
  #onTouched: () => T;

  #destroyRef = inject(DestroyRef);
  #changeDetector = inject(ChangeDetectorRef);

  /** the text input control where the user types his search query */
  filterControl: ExtendedFormControl;

  constructor() {}

  ngOnDestroy(): void {
    this.#submittedRef && this.#submittedRef.destroy();
  }

  ngOnInit(): void {
    this.mirrorControl();
    this.applyCustomChangeHandler();
    this.addOnResetLocalHandler();
  }

  /**
   * @description a wrapper function to add a *local* handler to the field's "onReset" hook (config)
   * this gives us a chance to reset and initialize local elements and members, for example: clear the
   * query text and the last selected option when the user resets the field's value
   */
  private addOnResetLocalHandler(): void {
    if (!isFunction(this.fieldConfig?.onReset)) {
      return;
    }

    this.fieldConfig.onReset = () => {
      this.onManualQueryReset();
    };
  }

  ngAfterViewInit(): void {
    this.applyFilteringBehavior();

    this.fieldAccessor.control.markAsPristine();
    this.fieldAccessor.control.markAsUntouched();
    this.#changeDetector.detectChanges();
  }

  /** writes a value to the form control attached to the filter's query textbox */
  writeFilterValue(selectedOption: TIterableOption | undefined): void {
    this.filterControl.setValue(selectedOption);
    this.filterControl.updateValueAndValidity();
  }

  writeQueryText(selectedOption: TIterableOption | string | undefined): void {
    let query;

    if (isUndefined(selectedOption)) {
      query = undefined;
    } else if (isString(selectedOption)) {
      query = selectedOption;
    } else if (this.keyProp in selectedOption) {
      query = selectedOption[this.keyProp];
    }

    this.queryText.set(query);
    this.filterControl.updateValueAndValidity();
  }

  /** inform angular the control's new value */
  writeValue(value: T | undefined): void {
    this.value = value;

    if (this.value !== this.control?.value) {
      this.control.setValue(value);
    }
  }

  registerOnChange(fn: (value: T | undefined) => T): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => T): void {
    this.#onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.filterControl.disable() : this.filterControl.enable();
  }

  displayFn(option: TIterableOption): string {
    return option ? (option[this.keyProp] as string) : '';
  }

  /**
   * @description
   * user's manual option selection via tap | click event. (this is different from autoSelectOnBlur)
   * @param option deconstruction of MatAutocompleteSelectedEvent which holds the selected MatOption
   */
  onOptionClick({ option }: MatAutocompleteSelectedEvent): void {
    const selectedOption = option.value;

    if (this.#lastOption === selectedOption) {
      return;
    }

    this.selectOption(selectedOption);
  }

  /**
   * @description
   * executes when the user blurs the query textbox.
   * @param event the blur event object
   */
  onFilterBlur(event: Event) {
    this.markValueControlDirty();

    this.markFilterControlDirty();

    this.autoSelectOnBlur(this.fieldAccessor.options$$());

    this.#changeDetector.detectChanges();

    this.blur.emit();
  }

  /**
   * @description
   * perform a reset of the entire control and trigger a callback to owner. this method executes
   * when the user presses the X button of the filter input in order to clear the current query
   * value. We trigger the field's onReset hook, (if the user defined one) on the field's config.
   * Since this autocomplete does not manage the options list's source - it is the user's responsibility
   * to implement an onReset handler to synchronize the options list to [] (empty array) when the query
   * filter text is reset.
   */
  onManualQueryReset(): void {
    /** clear the query control (where the user types) */
    this.writeFilterValue(undefined);

    this.writeQueryText(undefined);

    /** clear value control (and local value) */
    this.resetValueAndOptions();

    this.launchResetCallback();
  }

  trackByValue(index: number, data: any): T {
    return data[this.valueProp];
  }

  // /**
  //  * @description
  //  * adds a local handler to the field's "onReset" hook (config). we need this to reset local values
  //  * like the query text & last selected option when the reset event is triggered outside the auto complete
  //  */
  // private addOnResetLocalHandler(): void {
  //   if (!isFunction(this.fieldAccessor.onReset)) {
  //     return;
  //   }

  //   const originalReset = this.fieldAccessor.onReset;

  //   const locals = {
  //     ...this.fieldAccessor.getFieldLocals(),
  //     ...{ option: this.#lastOption }
  //   };

  //   this.fieldAccessor.onReset = () => {
  //     originalReset({ ...locals });
  //     this.#lastOption = undefined;
  //     this.writeFilterValue(undefined);
  //     this.writeQueryText(undefined);
  //   };
  // }

  private selectOption(option: TIterableOption): void {
    if (!option) {
      this.resetValueAndOptions();
      return;
    }

    const selectedValue = option[this.valueProp];

    this.writeValue(selectedValue);

    this.writeQueryText(option);

    this.#onChange(selectedValue);

    this.#onTouched();

    this.triggerSelectCallback(option);

    this.#lastOption = option;

    this.#changeDetector.markForCheck();
  }

  private markFilterControlDirty() {
    this.filterControl?.markAsDirty();
    this.filterControl?.markAsTouched();
    this.filterControl?.updateValueAndValidity();
  }

  private markValueControlDirty() {
    this.control?.markAsDirty();
    this.control?.markAsTouched();
    this.control?.updateValueAndValidity();
  }

  /**
   * @description
   * access & launch the field's config "onReset" callback from inside the auto complete
   */
  private launchResetCallback() {
    /** check if user defined an onReset hook */
    if (!this.fieldAccessor?.onReset) {
      return;
    }

    /** prepare to run onReset hook */
    const { onReset, getFieldLocals } = this.fieldAccessor;

    /**
     * bind onReset hook, make the form field directive reference "this" instance inside callbacks
     * so that we gain its context and be able to finish the callbacks successfully
     * */
    const resetHandler = onReset.bind(this.fieldAccessor);

    /** get the form field's event hook default locals */
    const localsHandler = getFieldLocals.bind(this.fieldAccessor);

    /** run event  handler for reset hook, pass field's locals as args, in case we need them in handler */
    resetHandler(localsHandler());

    this.#changeDetector.detectChanges();
  }

  private resetValueAndOptions(): void {
    this.writeValue(undefined);
    this.resetOptions();
    this.#lastOption = null;
  }

  private resetOptions(): void {
    this.fieldAccessor.options$$.set([]);
  }

  /**
   * @description
   * each form field potentially include a control.valueChange subscription to alert
   * consumers of its changing value, however in the auto complete context we use the
   * valueChange handler to filter the list results, and the onSelect method to alert
   * consumer of the selected value change. if we wont cancel the form control's
   * valueChange handler it will fire on select & on change.
   */
  private applyCustomChangeHandler(): void {
    this.fieldAccessor.cancelValueChangeHandler();
  }

  /**
   * @description
   * there are two controls in this component, the original form control - the one
   * stored inside this.fieldConfig.control - which value's binded to the form, and
   * the other one is the control where the user types his search query. we need to
   * validate both of them in order to implement the desired user experience. here
   * we copy the original field's validators and apply them to the filter query control
   */
  private mirrorControl(): void {
    const { validationHandlers } = this.validation;

    const queryValidators = validationHandlers;

    /** this validator will make sure the query typed in the filter input IS included in the options list */
    queryValidators.push(
      new AutoCompleteValidation({
        query$$: this.queryText,
        options$$: this.fieldAccessor?.options$$,
        keyProp: this.keyProp,
        valueProp: this.valueProp
      })
    );

    const filterValidators = [...composeValidators(queryValidators)];

    this.filterControl = new ExtendedFormControl({ disabled: this.disabled, value: '' }, { updateOn: 'blur' });

    this.filterControl.setValidators(filterValidators);

    this.filterControl.updateValueAndValidity();
  }

  /**
   * @description
   * when an option is selected - we inform consumers of this control about the change.
   * if an "onSelect" callback as provided by consumer we trigger it and pass the option
   * an an arg
   * @param selectedOption
   */
  private triggerSelectCallback(selectedOption: TIterableOption): void {
    /**
     * prepare the args for the "onSelect" callback. the args are typed as is every
     * field.update call, we need to pass prev & next values, and other local useful
     * data such as the config scheme and the entire form
     */
    const locals = {
      ...this.fieldAccessor.getFieldLocals(),
      ...{ option: selectedOption }
    };

    const valuePair: IFormFieldValueChange<T> = {
      prevValue: (this.#lastOption && this.#lastOption[this.valueProp]) || undefined,
      nextValue: selectedOption[this.valueProp]
    };

    const { onSelect } = this.fieldAccessor;

    if (!isFunction(onSelect)) {
      return;
    }

    this.fieldAccessor.onSelect({ ...valuePair }, { ...locals });
  }

  private applyFilteringBehavior(): void {
    fromEvent<InputEvent>(this.filterElement, 'input')
      .pipe(
        startWith(null),
        debounceTime(this.debounceTime),
        takeUntilDestroyed(this.#destroyRef),
        distinctUntilChanged((prevValue, nextValue) => {
          return prevValue === nextValue;
        }),
        /** pick up the previous value and pair with current value */
        pairwise(),
        map(([prevEvent, nextEvent]: [InputEvent, InputEvent]) => {
          return {
            prevValue: (prevEvent?.target as any)?.value,
            nextValue: (nextEvent?.target as any)?.value
          };
        }),
        /** apply the custom callback function */
        filter(() => {
          if (this.changeTrigger === 'valid-only') {
            return this.control.valid;
          }
          return this.changeTrigger === 'always';
        }),
        /**
         * we switch from query typing event stream to triggering getting new data
         * its important to switch map to cover cases of getting data asynchronously
         * via http
         */
        tap(({ prevValue, nextValue }: IFormFieldValueChange<T>) => {
          this.writeQueryText(nextValue as string);
          this.isPending$$.set(true);
        }),
        switchMap(({ prevValue, nextValue }: IFormFieldValueChange<T>) => {
          return this.notifyQueryChange({ prevValue, nextValue });
        }),
        tap(({ options }: TFieldStateArgs<T>) => {
          this.control.updateValueAndValidity();
          this.filterControl.updateValueAndValidity();
        }),
        tap(() => this.isPending$$.set(false)),
        finalize(() => this.isPending$$.set(false))
      )
      .subscribe();
  }

  /**
   * @description
   * Auto selects an option on blur when blur event is NOT triggered via selecting an option.
   * in this scenario we auto select an option in the following cases:
   * (1) options.length === 1
   * (2) options.length > 1 & options contains an exact match to the current query
   * @param options an array of latest options collection
   */
  private autoSelectOnBlur(options: TIterableOption[] = []): void {
    const { length } = options;

    let autoSelectedOption;

    if (length === 1) {
      autoSelectedOption = options[0];
    } else if (length > 1) {
      autoSelectedOption = this.matchOptionForQuery(options);
    } else {
      return;
    }

    if (isUndefined(autoSelectedOption)) {
      return;
    }

    this.selectOption(autoSelectedOption);

    this.writeFilterValue(autoSelectedOption);

    this.writeQueryText(autoSelectedOption);

    this.#changeDetector.detectChanges();
  }

  /**
   * @description
   * try to find a match between the current query and the options list. if the query is an exact match
   * return it, otherwise return undefined
   * @param options latest options array
   * @param query query text value
   * @returns option which matches query
   */
  private matchOptionForQuery(options: TIterableOption[], query = this.queryText()): TIterableOption | undefined {
    if (this.hasUserSelectedOption()) {
      return undefined;
    }

    const exactMatchOption = options.find((option) => {
      const matchKey = option[this.keyProp] === query;
      if (this.queryScope() === 'keys-values') {
        const matchValue = option[this.valueProp] == query;
        return matchKey || matchValue;
      }
      return matchKey;
    });

    return exactMatchOption;
  }

  /**
   * @description
   * while typing (and not yet selecting) - filter control's value is a string(!).
   * once selected - it becomes the selected option(!) we use this difference to identify
   * if a user blurred the filter control without selecting an option
   */
  private hasUserSelectedOption(): boolean {
    return !isString(this.filterControl.value);
  }

  /**
   * @description
   * this method is called whenever the user types a query - thus triggering the need to update
   * the options. if the options source is an http call its important that this method will return
   * an observable, which we pipe to the key press event stream, allowing us to cancel on-going
   * http calls whenever a new key press event arrives before previous http call is complete.
   *
   * @param { prevValue, nextValue } - a deconstruction of a field's value change callback
   * @returns as observable of field state
   */
  private notifyQueryChange({ prevValue, nextValue }: IFormFieldValueChange<T>): Observable<TFieldStateArgs<T>> {
    const locals = {
      ...this.fieldAccessor.getFieldLocals()
    };

    /** request more options from consumer */
    const fieldState$ = this.fieldAccessor.fieldConfig.valueChange({ prevValue, nextValue }, { ...locals });

    /** the value change usually won't return a value if the options are updated locally */
    if (!isObservable(fieldState$)) {
      const { options } = this.fieldAccessor.fieldConfig.state$$();
      return of({ options });
    }

    this.#changeDetector.markForCheck();

    /** here we know we have new options which came from an http call */
    return fieldState$ as Observable<TFieldStateArgs<T>>;
  }
}
